import React, { useEffect, useRef, useState } from 'react';
import {
  BookOpenText,
  Eye,
  ImagePlus,
  Newspaper,
  Pencil,
  Plus,
  RefreshCcw,
  Save,
  ShieldAlert,
  Trash2,
  UploadCloud,
  X
} from 'lucide-react';
import { Navigate } from 'react-router-dom';

import { ConfirmModal } from '../../components/ConfirmModal';
import FloatingField from '../../components/FloatingField';
import NewsDetailModal from '../../components/NewsDetailModal';
import { API_URL } from '../../config';
import useToast from '../../hooks/useToast';
import { createNews, deleteNews, getManageNewsList, updateNews } from '../../services/newsApi';
import { APP_VERSION } from '../../generated/appVersion';
import { compareVersions } from '../../utils/appUpdates';
import { compressImageFile } from '../../utils/imageCompression';
import { formatTimezoneDate, TIMEZONE_CHANGED_EVENT } from '../../utils/timezone';
import { haptic } from '../../utils/vibration';
import { useInteractionPreferences } from '../../contexts/InteractionPreferencesContext';

const USER_TYPE_OPTIONS = [
  { id: 'user', label: 'Пользователь' },
  { id: 'dispatcher', label: 'Диспетчер' },
  { id: 'admin', label: 'Администратор' },
  { id: 'developer', label: 'Разработчик' }
];

const CATEGORY_OPTIONS = [
  { id: 'news', label: 'Новость', icon: Newspaper, badgeClassName: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300' },
  { id: 'instruction', label: 'Инструкция', icon: BookOpenText, badgeClassName: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300' },
  { id: 'update', label: 'Обновление', icon: RefreshCcw, badgeClassName: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300' }
];

const emptyForm = {
  title: '',
  category: 'news',
  app_version: '',
  min_supported_version: '',
  is_required_update: false,
  title_image_url: '',
  content: '',
  target_user_types: [],
  target_roles: [],
  target_departments: [],
  target_stages: [],
  is_active: true
};

const formatDateTime = (value) => {
  const formatted = formatTimezoneDate(value, true);
  return formatted ? formatted.slice(0, 16) : '';
};

const resolveNewsImageUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:') || !raw.startsWith('/')) {
    return raw;
  }
  return `${API_URL}${raw}`;
};

const toggleValue = (values, nextValue) => (
  values.includes(nextValue)
    ? values.filter((item) => item !== nextValue)
    : [...values, nextValue]
);

const getAudienceLabel = (item) => {
  const raw = String(item?.target_summary || '').trim();
  return raw || 'Видно всем';
};

const getContentPreview = (content) => {
  const normalized = String(content || '')
    .replace(/!\[(.*?)\]\((.*?)\)/g, ' ')
    .replace(/^#{1,2}\s+/gm, '')
    .replace(/^- /gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized;
};

const getCategoryOption = (category) => CATEGORY_OPTIONS.find((option) => option.id === category) || CATEGORY_OPTIONS[0];

const getUpdateVersionError = (form) => {
  if (form.category !== 'update') return '';
  const appVersion = String(form.app_version || '').trim();
  const minSupportedVersion = String(form.min_supported_version || '').trim();
  if (!appVersion || !minSupportedVersion) return '';
  return compareVersions(minSupportedVersion, appVersion) > 0
    ? 'Минимальная версия не может быть выше версии релиза'
    : '';
};

const buildDraftPreviewItem = (form, editingId) => ({
  id: editingId || 'draft',
  title: form.title.trim() || 'Без заголовка',
  category: form.category || 'news',
  app_version: form.app_version || null,
  min_supported_version: form.min_supported_version || null,
  is_required_update: form.is_required_update,
  title_image_url: form.title_image_url || '',
  content: form.content.trim() || 'Текст публикации пока пуст.',
  created_at: new Date().toISOString()
});

const uploadNewsImage = async (file) => {
  const preparedFile = await compressImageFile(file);
  const formData = new FormData();
  formData.append('file', preparedFile);
  const response = await fetch(`${API_URL}/api/news/upload-image`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    },
    body: formData
  });
  const contentType = response.headers.get('content-type') || '';
  let data = {};
  let rawText = '';

  if (contentType.includes('application/json')) {
    data = await response.json().catch(() => ({}));
  } else {
    rawText = await response.text().catch(() => '');
  }

  if (!response.ok) {
    if (response.status === 413) {
      throw new Error('Изображение слишком большое. Максимум 10 МБ');
    }
    throw new Error(
      data.detail ||
      (rawText.includes('413 Request Entity Too Large') ? 'Изображение слишком большое. Максимум 10 МБ' : '') ||
      'Не удалось загрузить изображение'
    );
  }
  return data.url;
};

const TargetGroup = ({ title, options, selected, onToggle }) => (
  <div className="space-y-3 rounded-xl border border-gray-200 p-3 dark:border-gray-800">
    <div className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">{title}</div>
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const id = option.id ?? option;
        const label = option.label ?? option;
        const active = selected.includes(id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => {
              haptic('selection');
              onToggle(id);
            }}
            className={`inline-flex items-center rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
              active
                ? 'bg-blue-600 text-white shadow-sm'
                : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  </div>
);

const NewsManagement = () => {
  const { animationsEnabled } = useInteractionPreferences();
  const { toast, showToast, hideToast } = useToast(3200);
  const userType = localStorage.getItem('user_type');
  const titleImageInputRef = useRef(null);
  const bodyImageInputRef = useRef(null);
  const textareaRef = useRef(null);

  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [, setTimezoneVersion] = useState(0);
  const updateVersionError = getUpdateVersionError(form);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const newsData = await getManageNewsList();
        if (cancelled) return;
        setItems(Array.isArray(newsData?.items) ? newsData.items : []);
      } catch (err) {
        if (!cancelled) {
          haptic('error');
          showToast(err.message || 'Не удалось загрузить публикации', 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshVersion]);

  useEffect(() => {
    const handleTimezoneChanged = () => {
      setTimezoneVersion((prev) => prev + 1);
    };

    window.addEventListener(TIMEZONE_CHANGED_EVENT, handleTimezoneChanged);
    return () => window.removeEventListener(TIMEZONE_CHANGED_EVENT, handleTimezoneChanged);
  }, []);

  if (userType !== 'developer') {
    return <Navigate to="/" replace />;
  }

  const clearForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const resetForm = () => {
    haptic('close');
    clearForm();
  };

  const openPreview = (item) => {
    haptic('open');
    setPreviewItem(item);
  };

  const closePreview = () => {
    haptic('close');
    setPreviewItem(null);
  };

  const applyEdit = (item) => {
    haptic('open');
    setEditingId(item.id);
    setForm({
      title: item.title || '',
      category: item.category || 'news',
      app_version: item.app_version || '',
      min_supported_version: item.min_supported_version || '',
      is_required_update: Boolean(item.is_required_update),
      title_image_url: item.title_image_url || '',
      content: item.content || '',
      target_user_types: item.target_user_types || [],
      target_roles: item.target_roles || [],
      target_departments: item.target_departments || [],
      target_stages: item.target_stages || [],
      is_active: item.is_active !== false
    });
    window.scrollTo({ top: 0, behavior: animationsEnabled ? 'smooth' : 'auto' });
  };

  const openDeleteConfirm = (item) => {
    haptic('warning');
    setPendingDeleteItem(item);
  };

  const closeDeleteConfirm = () => {
    haptic('close');
    setPendingDeleteItem(null);
  };

  const handleBodyImageUpload = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const url = await uploadNewsImage(file);
      const textarea = textareaRef.current;
      const insertion = `\n\n![Изображение](${url})\n\n`;

      if (!textarea) {
        setForm((prev) => ({ ...prev, content: `${prev.content}${insertion}`.trim() }));
      } else {
        const start = textarea.selectionStart ?? form.content.length;
        const end = textarea.selectionEnd ?? start;
        const nextContent = `${form.content.slice(0, start)}${insertion}${form.content.slice(end)}`;
        setForm((prev) => ({ ...prev, content: nextContent }));
      }

      haptic('success');
      showToast('Изображение добавлено в текст', 'success');
    } catch (err) {
      haptic('error');
      showToast(err.message || 'Не удалось загрузить изображение', 'error');
    } finally {
      setSaving(false);
      if (bodyImageInputRef.current) bodyImageInputRef.current.value = '';
    }
  };

  const handleTitleImageUpload = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const url = await uploadNewsImage(file);
      setForm((prev) => ({ ...prev, title_image_url: url }));
      haptic('success');
      showToast('Обложка загружена', 'success');
    } catch (err) {
      haptic('error');
      showToast(err.message || 'Не удалось загрузить обложку', 'error');
    } finally {
      setSaving(false);
      if (titleImageInputRef.current) titleImageInputRef.current.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (updateVersionError) {
      haptic('error');
      showToast(updateVersionError, 'error');
      return;
    }
    setSaving(true);

    const payload = {
      title: form.title.trim(),
      category: form.category || 'news',
      app_version: form.category === 'update' ? form.app_version.trim() : '',
      min_supported_version: form.category === 'update' ? form.min_supported_version.trim() : '',
      is_required_update: form.category === 'update' && form.is_required_update,
      content: form.content.trim(),
      title_image_url: form.title_image_url.trim() || null,
      target_user_types: form.target_user_types,
      target_roles: [],
      target_departments: [],
      target_stages: [],
      is_active: form.is_active
    };

    try {
      const saved = editingId ? await updateNews(editingId, payload) : await createNews(payload);
      setItems((prev) => (editingId ? prev.map((item) => (item.id === editingId ? saved : item)) : [saved, ...prev]));
      haptic('success');
      showToast(editingId ? 'Публикация сохранена' : 'Публикация создана', 'success');
      clearForm();
    } catch (err) {
      haptic('error');
      showToast(err.message || 'Не удалось сохранить публикацию', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDeleteItem) return;
    try {
      await deleteNews(pendingDeleteItem.id);
      setItems((prev) => prev.filter((item) => item.id !== pendingDeleteItem.id));
      if (editingId === pendingDeleteItem.id) clearForm();
      haptic('success');
      showToast('Публикация удалена', 'success');
      setPendingDeleteItem(null);
    } catch (err) {
      haptic('error');
      showToast(err.message || 'Не удалось удалить публикацию', 'error');
    }
  };

  return (
    <div className="space-y-3 animate-fade-in desktop-content-container-wide">
      <form onSubmit={handleSubmit} className="space-y-4 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 animate-slide-up">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
          <div className="grid w-full min-w-0 grid-cols-3 rounded-xl bg-gray-100 p-1 dark:bg-gray-800 sm:w-auto">
            {CATEGORY_OPTIONS.map((option) => {
              const Icon = option.icon;
              const active = form.category === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setForm((prev) => ({
                      ...prev,
                      category: option.id,
                      app_version: option.id === 'update' ? (prev.app_version || APP_VERSION) : prev.app_version,
                      is_required_update: option.id === 'update' ? prev.is_required_update : false
                    }));
                  }}
                  className={`inline-flex min-w-0 items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs font-medium transition-colors sm:gap-2 sm:px-3 sm:text-sm ${
                    active
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-300'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 truncate">{option.label}</span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {editingId ? (
              <span className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                Редактирование
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => {
                haptic('selection');
                setForm((prev) => ({ ...prev, is_active: !prev.is_active }));
              }}
              className={`inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                form.is_active
                  ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:ring-emerald-900/50'
                  : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700'
              }`}
            >
              {form.is_active ? 'Активна' : 'Скрыта'}
            </button>
          </div>
        </div>

        <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.2fr)_320px]">
          <div className="min-w-0 space-y-4">
            <FloatingField label="Заголовок">
              <input
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                placeholder={
                  form.category === 'instruction'
                    ? 'Например: Как оформить повторный заказ'
                    : form.category === 'update'
                      ? `Например: Версия ${form.app_version || APP_VERSION}`
                      : 'Например: Изменения в выдаче заказов'
                }
                required
              />
            </FloatingField>

            <div className="block">
              <div className="mb-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    haptic('tap');
                    bodyImageInputRef.current?.click();
                  }}
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  <ImagePlus className="h-4 w-4" />
                  Картинка
                </button>
              </div>
              <FloatingField label="Текст">
                <textarea
                  ref={textareaRef}
                  value={form.content}
                  onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                  rows={14}
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder="Текст публикации. Можно использовать заголовки через # и списки через -"
                  required
                />
              </FloatingField>
              <input ref={bodyImageInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleBodyImageUpload(e.target.files?.[0])} />
            </div>
          </div>

          <div className="min-w-0 space-y-4">
            {form.category === 'update' ? (
              <div className="min-w-0 space-y-3 overflow-hidden rounded-xl border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/50 dark:bg-amber-950/10">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-200">
                  <RefreshCcw className="h-4 w-4" />
                  Параметры обновления
                </div>

                <FloatingField label="Версия релиза">
                  <input
                    value={form.app_version}
                    onChange={(e) => setForm((prev) => ({ ...prev, app_version: e.target.value }))}
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-amber-500 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                    placeholder={APP_VERSION}
                    required={form.category === 'update'}
                  />
                </FloatingField>

                <div>
                  <FloatingField label="Минимальная поддерживаемая версия" error={!!updateVersionError}>
                    <input
                    value={form.min_supported_version}
                    onChange={(e) => setForm((prev) => ({ ...prev, min_supported_version: e.target.value }))}
                    className={`w-full min-w-0 rounded-xl border bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition dark:bg-gray-900 dark:text-white ${
                      updateVersionError
                        ? 'border-red-500 focus:border-red-500 dark:border-red-500'
                        : 'border-gray-300 focus:border-amber-500 dark:border-gray-700'
                    }`}
                    placeholder="Не задана"
                    aria-invalid={Boolean(updateVersionError)}
                    aria-describedby={updateVersionError ? 'min-supported-version-error' : 'min-supported-version-help'}
                    />
                  </FloatingField>
                  <span
                    id={updateVersionError ? 'min-supported-version-error' : 'min-supported-version-help'}
                    className={`mt-1.5 block text-xs leading-4 ${updateVersionError ? 'text-red-600 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'}`}
                  >
                    {updateVersionError || 'Ниже этой версии обновление станет обязательным.'}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setForm((prev) => ({ ...prev, is_required_update: !prev.is_required_update }));
                  }}
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                    form.is_required_update
                      ? 'bg-red-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <ShieldAlert className="h-4 w-4" />
                  {form.is_required_update ? 'Обязательное обновление' : 'Можно отложить'}
                </button>
              </div>
            ) : null}

            <div className="space-y-3 rounded-xl border border-gray-200 p-3 dark:border-gray-800">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Обложка</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      haptic('tap');
                      titleImageInputRef.current?.click();
                    }}
                    className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    <UploadCloud className="h-4 w-4" />
                    Загрузить
                  </button>
                  {form.title_image_url ? (
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setForm((prev) => ({ ...prev, title_image_url: '' }));
                      }}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/20"
                      title="Убрать обложку"
                      aria-label="Убрать обложку"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                <input ref={titleImageInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleTitleImageUpload(e.target.files?.[0])} />
              </div>

              {form.title_image_url ? (
                <img src={resolveNewsImageUrl(form.title_image_url)} alt="Обложка публикации" className="h-44 w-full rounded-xl object-cover" />
              ) : (
                <div className="flex h-44 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400">
                  Обложка не выбрана
                </div>
              )}
            </div>

            <TargetGroup
              title="Кому показывать"
              options={USER_TYPE_OPTIONS}
              selected={form.target_user_types}
              onToggle={(value) => setForm((prev) => ({ ...prev, target_user_types: toggleValue(prev.target_user_types, value) }))}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
          {editingId ? (
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <X className="h-4 w-4" />
              Сбросить
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => openPreview(buildDraftPreviewItem(form, editingId))}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <Eye className="h-4 w-4" />
            Предпросмотр
          </button>
          <button
            type="submit"
            disabled={saving || Boolean(updateVersionError)}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
          >
            {editingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editingId ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </form>

      <div className="space-y-3 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 animate-slide-up">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Публикации</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRefreshVersion((prev) => prev + 1)}
              disabled={loading}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              title="Обновить статистику"
              aria-label="Обновить статистику"
            >
              <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {items.length}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            Публикаций пока нет
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => {
              const categoryMeta = getCategoryOption(item.category);
              const CategoryIcon = categoryMeta.icon;
              const contentPreview = getContentPreview(item.content);

              return (
                <div key={item.id} className="overflow-hidden rounded-xl border border-gray-200 p-3 dark:border-gray-800">
                  <div className="flex items-start gap-3">
                    {item.title_image_url ? (
                      <button
                        type="button"
                        onClick={() => openPreview(item)}
                        className="h-16 w-16 shrink-0 overflow-hidden rounded-xl"
                      >
                        <img src={resolveNewsImageUrl(item.title_image_url)} alt={item.title} className="h-full w-full object-cover" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openPreview(item)}
                        className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                      >
                        <CategoryIcon className="h-5 w-5" />
                      </button>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium ${categoryMeta.badgeClassName}`}>
                          <CategoryIcon className="h-3.5 w-3.5" />
                          {categoryMeta.label}
                        </span>
                        {item.category === 'update' && item.app_version ? (
                          <span className="rounded-lg bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                            v{item.app_version}
                          </span>
                        ) : null}
                        {item.category === 'update' && item.is_required_update ? (
                          <span className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1 text-[11px] font-medium text-red-700 dark:bg-red-950/30 dark:text-red-300">
                            <ShieldAlert className="h-3.5 w-3.5" />
                            Обязательно
                          </span>
                        ) : null}
                        <span className={`rounded-lg px-2 py-1 text-[11px] font-medium ${
                          item.is_active
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {item.is_active ? 'Активна' : 'Скрыта'}
                        </span>
                      </div>

                      <button type="button" onClick={() => openPreview(item)} className="mt-2 block w-full text-left">
                        <div className="line-clamp-2 break-words text-sm font-semibold leading-5 text-gray-900 dark:text-gray-100">
                          {item.title}
                        </div>
                        {contentPreview ? (
                          <div className="mt-1 line-clamp-2 break-words text-xs leading-5 text-gray-500 dark:text-gray-400">
                            {contentPreview}
                          </div>
                        ) : null}
                      </button>

                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                        <span>{formatDateTime(item.created_at)}</span>
                        <span className="min-w-0 max-w-full truncate">{getAudienceLabel(item)}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-medium">
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2 py-1 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
                          <Eye className="h-3.5 w-3.5" />
                          Просмотрели: {Number(item.unique_view_count) || 0}
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2 py-1 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                          <BookOpenText className="h-3.5 w-3.5" />
                          Прочитали: {Number(item.unique_read_count) || 0}
                        </span>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openPreview(item)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                        title="Предпросмотр"
                        aria-label="Предпросмотр"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => applyEdit(item)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                        title="Изменить"
                        aria-label="Изменить"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openDeleteConfirm(item)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-200 text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/20"
                        title="Удалить"
                        aria-label="Удалить"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <NewsDetailModal
        news={previewItem}
        isOpen={!!previewItem}
        onClose={closePreview}
        onConfirm={closePreview}
        confirmLabel="Закрыть"
      />

      <ConfirmModal
        isOpen={!!pendingDeleteItem}
        title="Удалить публикацию?"
        message={pendingDeleteItem ? `Публикация "${pendingDeleteItem.title}" будет удалена без возможности восстановления.` : ''}
        confirmText="Удалить"
        cancelText="Отмена"
        type="danger"
        onConfirm={handleDelete}
        onCancel={closeDeleteConfirm}
      />

          </div>
  );
};

export default NewsManagement;
